import { readCookieConsent } from "@/lib/legal/cookie-consent";

import { randomUuid } from "./uuid";

import {
  ANONYMOUS_ID_COOKIE,
  ANONYMOUS_ID_MAX_AGE_SECONDS,
  FIRST_TOUCH_LIST_COOKIE,
  IDENTITY_COOKIE_NAMES,
  SESSION_ID_COOKIE,
  SESSION_IDLE_SECONDS,
  isUuid,
  parseIdentityCookies,
  type IdentityCookies,
} from "./identity-cookies";

/**
 * The browser side of the identity cookies. See `identity-cookies.ts` for what they are and why
 * they exist only after the accept.
 *
 * Every function is best-effort: a browser that refuses cookies makes the visit anonymous, never an
 * error, and nothing here may break the page.
 */

/** `SameSite=Lax`, site-wide, `Secure` whenever the page is served over https. */
function write(name: string, value: string, maxAgeSeconds: number): void {
  const secure = typeof location !== "undefined" && location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${value}; Path=/; Max-Age=${maxAgeSeconds}; SameSite=Lax${secure}`;
}

/** What the browser holds now, whatever the consent (the stored answer is checked where it writes). */
export function readIdentityCookies(): IdentityCookies {
  try {
    return parseIdentityCookies(document.cookie);
  } catch {
    return {};
  }
}

/**
 * The identity for the dataLayer: what the cookies hold, and **nothing without the accept** — so
 * an id that outlived a refusal (another tab, a failed delete) still never reaches an event.
 */
export function readAnalyticsIdentity(): IdentityCookies {
  return readCookieConsent() === "aceito" ? readIdentityCookies() : {};
}

/**
 * Creates what is missing and renews the session, **only if the visitor accepted**.
 *
 * - `anonymous_id`: issued once and kept for twelve months;
 * - `first_touch_list_id`: written the first time a list is seen and **never overwritten**;
 * - `session_id`: the same value for the whole visit, its thirty minutes pushed forward on every call.
 *
 * Calling it is "there was activity".
 */
export function ensureIdentityCookies(context: { listId?: string | null } = {}): IdentityCookies {
  if (readCookieConsent() !== "aceito") return {};

  try {
    const current = readIdentityCookies();

    const anonymousId = current.anonymousId ?? randomUuid();
    if (current.anonymousId === undefined) write(ANONYMOUS_ID_COOKIE, anonymousId, ANONYMOUS_ID_MAX_AGE_SECONDS);

    let firstTouchListId = current.firstTouchListId;
    if (firstTouchListId === undefined && isUuid(context.listId)) {
      firstTouchListId = context.listId;
      write(FIRST_TOUCH_LIST_COOKIE, firstTouchListId, ANONYMOUS_ID_MAX_AGE_SECONDS);
    }

    const sessionId = current.sessionId ?? randomUuid();
    write(SESSION_ID_COOKIE, sessionId, SESSION_IDLE_SECONDS);

    return { anonymousId, ...(firstTouchListId ? { firstTouchListId } : {}), sessionId };
  } catch {
    return {};
  }
}

/** Deletes the three, for a refusal that follows an accept. Touches nothing that is not there. */
export function clearIdentityCookies(): void {
  try {
    const present = new Set(
      document.cookie
        .split(";")
        .map((part) => part.slice(0, Math.max(part.indexOf("="), 0)).trim())
        .filter((name) => name !== ""),
    );

    for (const name of IDENTITY_COOKIE_NAMES) {
      if (present.has(name)) write(name, "", 0);
    }
  } catch {
    // Nothing to do: a browser that refuses cookie writes has none to delete.
  }
}
